import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import type { AccommodationMonthView } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarMonthPicker'
import AccommodationCalendarMonthPicker from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarMonthPicker'
import ModalContext from 'contexts/ModalContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { groupBy, min } from 'lib/array/arrayUtils'
import { startOfDay, startOfMonth } from 'lib/datetime/dateUtils'
import { getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import WaitingListRequestFormModal from '../WaitingListRequest/WaitingListRequestFormModal'
import extractTourV2PurchasableOptionDetails from 'lib/tours/extractTourV2PurchasableOptionDetails'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import getTourV2DepartureDisplayPrice from 'lib/tours/getTourV2DepartureDisplayPrice'

interface Props {
  onSelect?: (selectedDate: Date) => void
  displayMemberPrice?: boolean
  travellers: Array<OfferPageRoom>
  offer: App.Tours.TourV2Offer
  departures: Array<App.Tours.TourV2OfferDeparture>
}

function TourV2DeparturesMonthsCalendar(props: Props) {
  const {
    onSelect,
    displayMemberPrice,
    travellers,
    offer,
    departures,
  } = props
  const intl = useIntl()

  const views = useMemo<Array<AccommodationMonthView>>(() => {
    const departuresByMonth = groupBy(departures, dep => +startOfMonth(new Date(dep.startDate)))

    return departuresByMonth.entries().map<AccommodationMonthView>(([timeStamp, departures]) => {
      const date = new Date(timeStamp)
      const departureRoomOptions = departures.flatMap(dep => getDepartureOptionsForTravellers(dep, travellers))
      // Trying to find the cheapest price - make sure it actually has one first
      const availableOptions = departureRoomOptions.map(roomOption => roomOption.cheapestOption).filter(option => !!getTourV2DepartureDisplayPrice(option, !!displayMemberPrice))
      // Now find the cheapest one
      const cheapestOption = min(availableOptions, option => getTourV2DepartureDisplayPrice(option, !!displayMemberPrice))
      const cheapestDeparture = departures.find(dep => dep.id === cheapestOption?.fkDepartureId)
      const hasDiscount = availableOptions.some(option => extractTourV2PurchasableOptionDetails(option).hasDiscount)

      const view: AccommodationMonthView = {
        // Departure duration is the date gap; tour days include the final day.
        duration: cheapestDeparture ? cheapestDeparture.duration + 1 : 0,
        durationUnit: 'day',
        month: date.getMonth(),
        year: date.getFullYear(),
        soldOutExtraActionText: intl.formatMessage({
          id: '0KoKUvwvIK5S_nTaSF5n',
          defaultMessage: 'Join the waitlist',
          description: 'Action text shown on sold-out tour month card to join a waiting list',
        }),
        featured: hasDiscount,
        soldOut: !!cheapestOption,
      }

      if (cheapestOption) {
        const hasLimitedSpots = departureRoomOptions.every(roomOption => roomOption.cheapestOption.inventoryLeft <= 4)
        view.pricing = {
          price: getTourV2DepartureDisplayPrice(cheapestOption, !!displayMemberPrice),
          saleUnit: '/person',
          fees: [],
        }
        view.label = hasLimitedSpots ? (
          <UrgencyLabel variant="critical1">
            {intl.formatMessage({
              id: '2mXHxnBEK1KDpORDLtAz',
              defaultMessage: 'Limited availability',
              description: 'Label shown on tour departure month when spots are limited',
            })}
          </UrgencyLabel>
        ) : undefined
      }
      return view
    }).toArray()
  }, [departures, displayMemberPrice, intl, travellers])

  const showModal = useContext(ModalContext)
  const onMonthSelect = useCallback((view: AccommodationMonthView) => {
    if (view.pricing) {
      const date = startOfDay(new Date(view.year, view.month))
      onSelect?.(date)
    } else {
      showModal(<WaitingListRequestFormModal offer={offer} />)
    }
  }, [offer, onSelect, showModal])

  return <AccommodationCalendarMonthPicker
    views={views}
    onSelect={onMonthSelect}
    priceType={displayMemberPrice && !departures.some(departure => !!departure.ltleBundleId) ? 'member' : 'default'}
  />
}

type MappedProps = Pick<Props, 'displayMemberPrice'>

export default withOfferPageState<MappedProps, Props>((state) => ({
  displayMemberPrice: state.displayMemberPrice,
}), TourV2DeparturesMonthsCalendar)
